import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import { PaywallGate } from "./components/PaywallGate";

const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Projects = lazy(() => import("./pages/Projects"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportView = lazy(() => import("./pages/ReportView"));
const Admin = lazy(() => import("./pages/Admin"));
const Profile = lazy(() => import("./pages/Profile"));
const CRM = lazy(() => import("./pages/CRM"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Automations = lazy(() => import("./pages/Automations"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ProjectDirector = lazy(() => import("./pages/dept/ProjectDirector"));
const Marketing = lazy(() => import("./pages/dept/Marketing"));
const Trading = lazy(() => import("./pages/dept/Trading"));
const Revenue = lazy(() => import("./pages/dept/Revenue"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Refunds = lazy(() => import("./pages/legal/Refunds"));
const DivineFlow = lazy(() => import("./pages/DivineFlow"));

const queryClient = new QueryClient();

const Fallback = () => (
  <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
    Loading…
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Fallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refunds" element={<Refunds />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/:id" element={<ReportView />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/crm" element={<PaywallGate required="starter_plan" feature="CRM"><CRM /></PaywallGate>} />
                <Route path="/assistant" element={<PaywallGate required="pro_plan" feature="AI Assistant"><Assistant /></PaywallGate>} />
                <Route path="/assistant/:threadId" element={<PaywallGate required="pro_plan" feature="AI Assistant"><Assistant /></PaywallGate>} />
                <Route path="/automations" element={<PaywallGate required="pro_plan" feature="Automations"><Automations /></PaywallGate>} />
                <Route path="/dept/project-director" element={<ProjectDirector />} />
                <Route path="/dept/marketing" element={<Marketing />} />
                <Route path="/dept/trading" element={<Trading />} />
                <Route path="/dept/revenue" element={<Revenue />} />
                <Route path="/divineflow" element={<DivineFlow />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
